import { useEffect, useRef, useState, useCallback } from 'react';
import toast from 'react-hot-toast';

export default function useInactivityTimer(logout, timeout = 240000) {
  const timer = useRef();
  const warningTimer = useRef();
  const countdownInterval = useRef();
  const [timeLeft, setTimeLeft] = useState(null);
  const [showWarning, setShowWarning] = useState(false);
  const [isActive, setIsActive] = useState(true);

  const clearAllTimers = useCallback(() => {
    clearTimeout(timer.current);
    clearTimeout(warningTimer.current);
    clearInterval(countdownInterval.current);
  }, []);

  const resetTimer = useCallback(() => {
    if (!isActive) return;
    
    // Clear existing timers
    clearAllTimers();
    setShowWarning(false);

    // Start countdown display
    let remainingTime = timeout;
    setTimeLeft(remainingTime);

    // Start countdown interval
    countdownInterval.current = setInterval(() => {
      setTimeLeft(prevTime => {
        const newTime = prevTime - 1000;
        if (newTime <= 0) {
          clearInterval(countdownInterval.current);
          return 0;
        }
        return newTime;
      });
    }, 1000);

    // Set warning timer (30 seconds before logout)
    warningTimer.current = setTimeout(() => {
      setShowWarning(true);
      toast.error('You will be logged out in 30 seconds due to inactivity!', {
        duration: 30000,
        id: 'inactivity-warning'
      });
    }, timeout - 30000);

    // Set logout timer
    timer.current = setTimeout(() => {
      clearAllTimers();
      setTimeLeft(null);
      setShowWarning(false);
      logout();
    }, timeout);
  }, [logout, timeout, isActive, clearAllTimers]);

  const handleContinue = useCallback(() => {
    setShowWarning(false);
    toast.dismiss('inactivity-warning');
    resetTimer();
  }, [resetTimer]);

  const handleLogout = useCallback(() => {
    setShowWarning(false);
    toast.dismiss('inactivity-warning');
    clearAllTimers();
    logout();
  }, [logout, clearAllTimers]);

  useEffect(() => {
    if (!isActive) {
      clearAllTimers();
      setTimeLeft(null);
      setShowWarning(false);
      return;
    }

    // List of events that indicate user activity
    const events = ['mousemove', 'keydown', 'mousedown', 'touchstart', 'scroll', 'click'];

    events.forEach(event =>
      window.addEventListener(event, resetTimer, true)
    );

    resetTimer(); // Start timer on mount

    return () => {
      clearAllTimers();
      events.forEach(event =>
        window.removeEventListener(event, resetTimer, true)
      );
    };
  }, [resetTimer, isActive, clearAllTimers]);

  const formatTime = (ms) => {
    if (!ms || ms <= 0) return '0:00';
    const minutes = Math.floor(ms / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  return {
    timeLeft,
    showWarning,
    formatTime,
    handleContinue,
    handleLogout,
    setIsActive
  };
}
